import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Alert, Button, Page, PageHeader, Select, Table, backLinkClasses, type Column } from '@evogard/ui';
import { formatInstant, loadFailureMessage } from '@evogard/domain';
import type { BillingAwardLogRow } from '@evogard/contract';
import { api } from '../../../lib/api.js';
import { AWARDS_KEY, useAwardTenants } from './useAwards.js';

const PAGE = 50;

/**
 * O log da premiação de uma base — o `sync_log` do legado, com os atos humanos
 * junto: cada recálculo e de onde veio, cada conferência no SGA, cada
 * invalidação, fechamento e mudança de configuração, com quem fez.
 */
export function AwardsLogScreen() {
  const tenants = useAwardTenants();
  const [slug, setSlug] = useState('');
  const tenant = tenants.find((t) => t.slug === slug) ?? tenants[0];
  const [pages, setPages] = useState(1);

  const query = useQuery({
    enabled: tenant !== undefined,
    queryKey: [...AWARDS_KEY, 'log', tenant?.slug, pages],
    queryFn: async () => {
      const items: BillingAwardLogRow[] = [];
      let cursor: string | undefined;
      let next: string | null = null;
      for (let i = 0; i < pages; i++) {
        const r = await api.billingAwards.log({ params: { tenant: tenant!.slug }, query: { cursor, limit: PAGE } });
        if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'o log da premiação'));
        items.push(...r.body.items);
        next = r.body.next;
        if (next === null) break;
        cursor = next;
      }
      return { items, next };
    },
  });

  return (
    <Page measure="cheia">
      <PageHeader
        title="Log da premiação"
        back={
          <Link to="/billing/awards" className={backLinkClasses()}>
            Premiação da cobrança
          </Link>
        }
      />
      {tenants.length > 1 && (
        <div className="w-44">
          <Select
            label="Base"
            value={tenant?.slug ?? ''}
            onChange={(e) => {
              setSlug(e.target.value);
              setPages(1);
            }}
          >
            {tenants.map((t) => (
              <option key={t.slug} value={t.slug}>
                {t.name}
              </option>
            ))}
          </Select>
        </div>
      )}
      {query.error && <Alert tone="erro">{query.error.message}</Alert>}
      <Table<BillingAwardLogRow>
        label="O que aconteceu na premiação"
        empty="Nada registrado ainda nesta base."
        items={query.data?.items ?? []}
        rowKey={(r) => String(r.id)}
        loading={query.isLoading}
        paginate={false}
        columns={COLUMNS}
      />
      {query.data?.next && (
        <div>
          <Button variant="contorno" loading={query.isFetching} onClick={() => setPages((p) => p + 1)}>
            Carregar mais
          </Button>
        </div>
      )}
    </Page>
  );
}

const COLUMNS: Array<Column<BillingAwardLogRow>> = [
  { title: 'Quando', cell: (r) => <span className="whitespace-nowrap">{formatInstant(r.at)}</span> },
  {
    title: 'O que aconteceu',
    alignment: 'inicio',
    cell: (r) => (
      <span className="flex flex-col">
        <span className="font-medium text-texto">{r.title}</span>
        {r.details && <span className="text-xs text-texto-suave">{r.details}</span>}
      </span>
    ),
  },
  { title: 'Quem', alignment: 'inicio', cell: (r) => r.actor ?? <span className="text-texto-suave">Sistema</span> },
];
